/**
 * Modal for submitting in-app bug reports to the tenant's ticketing system
 * (Azure DevOps or Jira).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { toBlob } from 'html-to-image'
import {
  ArrowLeft,
  Bug,
  Camera,
  Eye,
  Loader2,
  Mic,
  MonitorPlay,
  Paperclip,
  Play,
  Send,
  SlidersHorizontal,
  Square,
  Trash2,
  Video,
  X,
  Info,
} from 'lucide-react'
import { bugReportsApi } from '../../services/api/bugReports'
import type { BugReportDraft, BugReportInputMode } from '../../services/api/bugReports'
import { useAudioRecording } from '@features/chat/hooks/useAudioRecording'
import { chatDictationApi } from '@services/api/chatDictation'
import { logger } from '@utils/logger'
import { clientUniqueId } from '@utils/secureRandom'
import { snapshotToJsonFile } from './captureBuffer'
import { getBugReportTargetName } from './bugReportPublicConfig'
import { useToast } from '../../components/Toast'
import { config } from '../../config'
import {
  BUG_REPORT_TITLE_EMOJI_ERROR,
  stripEmoji,
  validateBugReportTitle,
} from './bugTitleValidation'
import { SearchableDropdown } from '../../components/ui/SearchableDropdown'
import { useBodyScrollLock } from '@hooks/useBodyScrollLock'
import { BUGSNAP_UI_PROPS } from './bugsnapUi'
import { useShieldedTrigger } from './useShieldedTrigger'
import { deriveContext } from './deriveContext'
import { WORK_ITEM_TYPE_OPTIONS } from './workItemTypes'
import './BugReportModal.css'

interface AttachmentPreview {
  id: string
  file: File
  /** Object URL, set for anything the browser can render inline (images, video). */
  previewUrl?: string
  kind: 'screenshot' | 'recording' | 'upload'
}

const isVideoFile = (file: File) => file.type.startsWith('video/')
const isPreviewable = (file: File) => file.type.startsWith('image/') || isVideoFile(file)

interface BugReportModalProps {
  isOpen: boolean
  /** Tenant has a drafting model configured; without one the mic is not offered. */
  voiceDraftingEnabled?: boolean
  onClose: () => void
}

const SEVERITY_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: '1 - Critical', label: 'Critical' },
  { value: '2 - High', label: 'High' },
  { value: '3 - Medium', label: 'Medium' },
  { value: '4 - Low', label: 'Low' },
]

/**
 * One-line-to-ticket stages, shared by the mic and the quick composer. The user
 * describes the problem once and the report files itself:
 * listening → drafting (AI fills every field) → review (short undo window).
 * 'listening' is voice-only; typing enters at 'drafting'.
 */
type VoicePhase = 'idle' | 'listening' | 'drafting' | 'review'

/**
 * Which half of the dialog is on screen. 'quick' is one box and one button —
 * the default whenever AI drafting is configured. 'detailed' is the full field
 * list, reached by "Add details", by a failed draft, and by every draft that
 * lands (so the fields being filed are on screen during the countdown).
 */
type ModalMode = 'quick' | 'detailed'

/**
 * Seconds the filled form is shown before it submits on its own. Long enough to
 * read every drafted field, since a click anywhere is what cancels the timer.
 */
const VOICE_COUNTDOWN_SECONDS = 10

/**
 * The quick composer's shorter window. The reporter typed the words themselves
 * a moment ago and is still looking at the dialog, so they need only long
 * enough to catch a wrong severity — not to re-read their own sentence. Any
 * shorter and the click that cancels would not land in time.
 */
const QUICK_COUNTDOWN_SECONDS = 4

/**
 * Cap on the quick composer, matching MAX_REPORT_CHARS in
 * bug_report_voice_service.py. Anything longer is truncated server-side before
 * drafting, so stopping the typing here keeps that invisible.
 */
const QUICK_TEXT_MAX_CHARS = 8000

/**
 * Indian English. Our reporters speak Indian English and Hinglish, which en-US
 * transcribes noticeably worse.
 */
const SPEECH_LANG = 'en-IN'

// Minimal shape of the Web Speech API. Typed by hand because the DOM lib does
// not ship these and `any` is an eslint error in this repo.
interface SpeechAlternative {
  transcript: string
}
interface SpeechResult {
  isFinal: boolean
  [index: number]: SpeechAlternative
}
interface SpeechResultEvent {
  results: { length: number; [index: number]: SpeechResult }
}
interface SpeechRecognitionLike {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  onresult: ((event: SpeechResultEvent) => void) | null
  onerror: ((event: { error?: string }) => void) | null
  onend: (() => void) | null
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

/** The browser's own recogniser. No token, no key, no backend call. */
function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function BugReportModal({
  isOpen,
  voiceDraftingEnabled = false,
  onClose,
}: BugReportModalProps) {
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState('')
  const [workItemType, setWorkItemType] = useState('Bug')
  const [featureGroup, setFeatureGroup] = useState('')
  const [workflowId, setWorkflowId] = useState('')
  const [feature, setFeature] = useState('')
  const [otherDetails, setOtherDetails] = useState('')
  const [suggestions, setSuggestions] = useState('')
  const [attachments, setAttachments] = useState<AttachmentPreview[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [capturingScreenshot, setCapturingScreenshot] = useState(false)
  const [recording, setRecording] = useState(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [descriptionTouched, setDescriptionTouched] = useState(false)
  const [severityTouched, setSeverityTouched] = useState(false)

  // ── Quick file ──────────────────────────────────────────────────
  // Without a drafting model there is nothing to turn one sentence into a
  // ticket, so the quick composer is not offered and the form opens directly.
  const [mode, setMode] = useState<ModalMode>(voiceDraftingEnabled ? 'quick' : 'detailed')
  const [quickText, setQuickText] = useState('')

  // ── One line to ticket ──────────────────────────────────────────
  const [voicePhase, setVoicePhase] = useState<VoicePhase>('idle')
  const [liveTranscript, setLiveTranscript] = useState('')
  const [draftSource, setDraftSource] = useState<BugReportDraft['source'] | null>(null)
  const [countdown, setCountdown] = useState(VOICE_COUNTDOWN_SECONDS)
  // The length this countdown started from. Held in state rather than read from
  // a constant so the digits and the draining bar agree on which window is
  // running — the quick composer's is shorter than the mic's.
  const [countdownFrom, setCountdownFrom] = useState(VOICE_COUNTDOWN_SECONDS)
  // Recognition delivers finals+interim as one growing string; keep the latest
  // in a ref so stopping can read it without waiting for a re-render.
  const liveTranscriptRef = useRef('')
  // Which capture path this take is using — decided once, when recording starts.
  const voiceModeRef = useRef<'browser' | 'batch'>('browser')
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)

  // Batch fallback for browsers with no recogniser (Firefox, older Safari):
  // record the take, then transcribe it server-side in one call.
  const recorder = useAudioRecording()

  // Support is a synchronous capability check, so no probe request is needed.
  const [speechSupported] = useState(() => getSpeechRecognitionCtor() !== null)
  // Browser capability only. Without a configured model the mic still works as
  // plain dictation into Description — see draftFromTranscript.
  const voiceSupported = speechSupported || recorder.isSupported

  // A take is in flight: the fields are about to be written by the draft, so
  // editing them now would only be overwritten. Everything below the header is
  // locked until the draft lands (or the take fails back to 'idle').
  const voiceBusy = voicePhase === 'listening' || voicePhase === 'drafting'
  const fieldsLocked = submitting || voiceBusy

  // Read by the unmount teardown, which cannot close over the latest state.
  const attachmentsRef = useRef(attachments)
  useEffect(() => {
    attachmentsRef.current = attachments
  }, [attachments])

  // Everything the dialog holds outside React is released here. The trigger
  // mounts this component only while open, so closing it *is* an unmount —
  // there is no `isOpen === false` render in which to clean up instead.
  useEffect(
    () => () => {
      // Recognition must never outlive the dialog. Clearing the ref first makes
      // the recogniser's own onend/onerror bail out.
      const recognition = recognitionRef.current
      recognitionRef.current = null
      try {
        recognition?.stop()
      } catch (err) {
        logger.debug('Bugsnap voice: error stopping recognition on unmount', {
          error: String(err),
        })
      }
      recorder.cancelRecording()

      // Screen capture would otherwise keep running with no UI left to stop it.
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }

      // Preview blobs are held by the document until revoked, so an unsubmitted
      // screenshot would leak for the rest of the session.
      attachmentsRef.current.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount-only teardown
    [],
  )

  // Lock document scroll while the form is open; allow scroll during screen
  // recording so the user can navigate the page being captured.
  useBodyScrollLock(isOpen && !recording)

  // Auto-detect Feature Group and Workflow/Agent ID from the current page on the
  // open transition, mirroring the auto reporter. Done via the "adjust state
  // while rendering" pattern (tracking the previous open state) rather than an
  // effect, so the pre-fill is applied before paint and the fields stay editable
  // afterwards. https://react.dev/learn/you-might-not-need-an-effect
  const [wasOpen, setWasOpen] = useState(false)
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen)
    if (isOpen) {
      const { featureGroup: fg, workflowId: wid } = deriveContext(window.location.href)
      if (fg) setFeatureGroup(fg)
      if (wid) setWorkflowId(wid)
    }
  }

  const addFile = useCallback((file: File, kind: AttachmentPreview['kind']) => {
    // Screen recordings get an object URL too, so the capture can be played back
    // before it is filed — a screenshot of the wrong page, or a recording of the
    // wrong tab, is only obvious once you can see it.
    const previewUrl = isPreviewable(file) ? URL.createObjectURL(file) : undefined
    setAttachments((prev) => [
      ...prev,
      { id: clientUniqueId('attachment'), file, previewUrl, kind },
    ])
  }, [])

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const item = prev.find((a) => a.id === id)
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  // Full-size preview. Held by id rather than by the item, so removing the
  // attachment — which revokes its object URL — closes the overlay by itself
  // instead of leaving a dead blob: src on screen.
  const [previewId, setPreviewId] = useState<string | null>(null)
  const previewItem = attachments.find((a) => a.id === previewId) ?? null
  const closePreview = useCallback(() => setPreviewId(null), [])

  useEffect(() => {
    if (!previewItem) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // The dialog has no Escape handler of its own today; stop the key here
        // anyway so adding one later cannot close the whole report along with
        // the preview.
        e.stopPropagation()
        setPreviewId(null)
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [previewItem])

  const captureScreenshot = useCallback(async () => {
    setCapturingScreenshot(true)
    const modalEl = document.querySelector('.bug-report-modal') as HTMLElement | null
    if (modalEl) modalEl.style.visibility = 'hidden'

    // Mark scrolled elements and apply monkey-patch to preserve scroll positions in html-to-image
    const scrolledElements: HTMLElement[] = []
    const allEls = document.querySelectorAll('*')
    for (let i = 0; i < allEls.length; i++) {
      const el = allEls[i] as HTMLElement
      if (el.scrollTop > 0 || el.scrollLeft > 0) {
        el.dataset.bugsnapScrolled = 'true'
        scrolledElements.push(el)
      }
    }

    const originalGetComputedStyle = window.getComputedStyle
    window.getComputedStyle = function (el: Element, pseudoElt?: string | null) {
      const style = originalGetComputedStyle(el, pseudoElt ?? undefined)
      
      if (!pseudoElt && el.parentElement instanceof HTMLElement && el.parentElement.dataset.bugsnapScrolled != null) {
        const sx = el.parentElement.scrollLeft
        const sy = el.parentElement.scrollTop
        
        if (sx > 0 || sy > 0) {
          return new Proxy(style, {
            get(target: CSSStyleDeclaration, prop: string) {
              if (prop === 'cssText') {
                return '' // force getPropertyValue fallback so our transform is read
              }
              if (prop === 'getPropertyValue') {
                return (propName: string) => {
                  let val = target.getPropertyValue(propName)
                  if (propName === 'transform') {
                    if (target.getPropertyValue('position') !== 'fixed') {
                      const trans = `translate(${-sx}px, ${-sy}px)`
                      val = (!val || val === 'none') ? trans : `${val} ${trans}`
                    }
                  }
                  return val
                }
              }
              const val = target[prop]
              return typeof val === 'function' ? val.bind(target) : val
            }
          })
        }
      }
      return style
    }

    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
      const blob = await toBlob(document.body, {
        // NOTE: do NOT enable `cacheBust` — it appends `?<timestamp>` to every
        // resource URL it inlines, which is invalid for blob: URLs (e.g. canvas
        // images on the workflows/canvas pages) and makes the whole capture
        // throw. Pages without blob assets happened to work, masking the bug.
        pixelRatio: 1,
        // If a single asset (cross-origin or blob image) can't be inlined, fall
        // back to a 1x1 transparent PNG instead of failing the entire capture.
        imagePlaceholder:
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
        filter: (node) => {
          if (node instanceof HTMLElement && node.classList.contains('bug-report-modal')) {
            return false
          }
          return true
        },
      })
      if (!blob) throw new Error('Screenshot capture failed')
      const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' })
      addFile(file, 'screenshot')
      toast.success('Screenshot captured')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Screenshot failed')
    } finally {
      window.getComputedStyle = originalGetComputedStyle
      scrolledElements.forEach(el => delete el.dataset.bugsnapScrolled)
      if (modalEl) modalEl.style.visibility = ''
      setCapturingScreenshot(false)
    }
  }, [addFile, toast])

  // Mirrors the Screenshot button's own disabled condition: the shield activates on
  // `mousedown`, so it cannot rely on the browser suppressing clicks.
  const handleCaptureScreenshot = useCallback(() => {
    if (submitting || capturingScreenshot || recording) return
    void captureScreenshot()
  }, [submitting, capturingScreenshot, recording, captureScreenshot])

  // Shielded like the Bugsnap trigger itself: pressing Screenshot must not reach the
  // page's document-level listeners, or the row menu / dropdown being reported would
  // close in the instant before the capture runs. See ./useShieldedTrigger.
  const screenshotBtnRef = useShieldedTrigger<HTMLButtonElement>(handleCaptureScreenshot)

  const startScreenRecording = async () => {
    try {
      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        })
      } catch {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
        })
      }
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm')
          ? 'video/webm'
          : MediaRecorder.isTypeSupported('video/mp4')
            ? 'video/mp4'
            : ''
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      recordedChunksRef.current = []
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recordedChunksRef.current.push(ev.data)
      }
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const actualMime = mimeType || recorder.mimeType || 'video/webm'
        const blob = new Blob(recordedChunksRef.current, { type: actualMime })
        if (blob.size > 0) {
          const ext = actualMime.includes('mp4') ? 'mp4' : 'webm'
          const file = new File([blob], `recording-${Date.now()}.${ext}`, { type: actualMime })
          addFile(file, 'recording')
          toast.success('Recording saved')
        }
        setRecording(false)
      }
      const [videoTrack] = stream.getVideoTracks()
      if (videoTrack) {
        videoTrack.addEventListener('ended', () => {
          if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop()
          } else {
            setRecording(false)
          }
        })
      }
      mediaRecorderRef.current = recorder
      recorder.start(1000)
      setRecording(true)
      toast.info('Recording… click Stop when finished')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start screen recording')
    }
  }

  const stopScreenRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
  }

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files
    if (!list?.length) return
    Array.from(list).forEach((file) => addFile(file, 'upload'))
    e.target.value = ''
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const sanitized = stripEmoji(raw)
    setTitle(sanitized)
    setTitleError(raw !== sanitized ? BUG_REPORT_TITLE_EMOJI_ERROR : validateBugReportTitle(sanitized))
  }
  // Title uses immediate emoji/character validation (stateful titleError);
  // description and severity use the required-on-touch/submit pattern.
  const descriptionError = !description.trim() ? 'Description is required' : ''
  const severityError = !severity ? 'Severity is required' : ''

  const showDescriptionError = descriptionTouched && !!descriptionError
  const showSeverityError = severityTouched && !!severityError

  const submitReport = useCallback(async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required')
      return
    }
    const titleValidationError = validateBugReportTitle(title)
    if (titleValidationError) {
      setTitleError(titleValidationError)
      toast.error(titleValidationError)
      return
    }

    setSubmitting(true)
    try {
      const files = attachments.map((a) => a.file)
      const contextFile = snapshotToJsonFile()
      if (contextFile) files.push(contextFile)

      const result = await bugReportsApi.submit({
        title: title.trim(),
        description: description.trim(),
        pageUrl: window.location.href,
        severity: severity || undefined,
        workItemType: workItemType || undefined,
        featureGroup: featureGroup.trim() || undefined,
        workflowId: workflowId.trim() || undefined,
        feature: feature.trim() || undefined,
        otherDetails: otherDetails.trim() || undefined,
        suggestions: suggestions.trim() || undefined,
        userAgent: navigator.userAgent,
        files,
      })
      if (result.work_item_url) {
        toast.success(result.message || `Work item #${result.work_item_id} created`)
      } else {
        toast.success(result.message || 'Bugsnap submitted')
      }
      window.dispatchEvent(new CustomEvent('bugsnap:created'))
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit Bugsnap')
    } finally {
      setSubmitting(false)
    }
  }, [
    title,
    description,
    severity,
    workItemType,
    featureGroup,
    workflowId,
    feature,
    otherDetails,
    suggestions,
    attachments,
    toast,
    onClose,
  ])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setVoicePhase('idle')
    void submitReport()
  }

  // ── One line to ticket ──────────────────────────────────────────

  /** Fill every field from the AI draft, holding the title to the typed rules. */
  const applyDraft = useCallback((draft: BugReportDraft) => {
    const cleanTitle = stripEmoji(draft.title).slice(0, 255)
    setTitle(cleanTitle)
    setTitleError(validateBugReportTitle(cleanTitle))
    setDescription(draft.description)
    setSeverity(draft.severity)
    setWorkItemType(draft.work_item_type || 'Bug')
    if (draft.feature) setFeature(draft.feature)
    if (draft.suggestions) setSuggestions(draft.suggestions)
    if (draft.other_details) setOtherDetails(draft.other_details)
    // The fields are filled, so the required-on-touch hints would be noise.
    setDescriptionTouched(true)
    setSeverityTouched(true)
    setDraftSource(draft.source)
  }, [])

  /**
   * The one path from raw words to a filed ticket, shared by the mic and the
   * quick composer: draft every field, then hand the user a countdown to catch
   * anything wrong before it goes to the board.
   *
   * `inputMode` only picks the server-side prompt (speech arrives conversational
   * and with recogniser noise; typing arrives terse). `countdownSeconds` is the
   * caller's, because the quick composer's window is shorter than the mic's.
   */
  const draftAndArm = useCallback(
    async (
      rawText: string,
      { inputMode, countdownSeconds }: { inputMode: BugReportInputMode; countdownSeconds: number },
    ) => {
      const text = rawText.trim()
      if (!text) {
        setVoicePhase('idle')
        toast.error(
          inputMode === 'typed'
            ? 'Describe the issue first.'
            : 'No speech detected. Please try again.',
        )
        return
      }
      // No drafting model configured for the tenant: the mic still earns its
      // place as plain dictation. Skip the round trip that would only 503. The
      // quick composer is not offered at all in this state, so only speech
      // reaches here.
      if (!voiceDraftingEnabled) {
        setDescription((prev) => (prev.trim() ? `${prev}\n\n${text}` : text))
        setDescriptionTouched(true)
        setLiveTranscript('')
        liveTranscriptRef.current = ''
        setMode('detailed')
        setVoicePhase('idle')
        toast.info('Added to Description. Fill in the remaining fields to submit.')
        return
      }

      setVoicePhase('drafting')
      try {
        const draft = await bugReportsApi.draftTicket({
          text,
          inputMode,
          pageUrl: window.location.href,
        })
        applyDraft(draft)
        // The words have become the filled fields, so neither the live
        // transcript nor the composer has anything left to show — drop them
        // rather than repeat the form back.
        setLiveTranscript('')
        liveTranscriptRef.current = ''
        setQuickText('')
        // Show what is about to be filed: the countdown is only a real review
        // window if the drafted fields are on screen while it runs.
        setMode('detailed')
        setCountdownFrom(countdownSeconds)
        setCountdown(countdownSeconds)
        setVoicePhase('review')
        // No toast here: the same sentence now runs beside the countdown bar in
        // the footer, where it stays put and sits next to the fields it refers
        // to, instead of covering the dialog from the corner.
      } catch (err) {
        // Never lose words the user already gave us — park them in Description
        // so the report can still be finished by hand.
        setDescription((prev) => (prev.trim() ? `${prev}\n\n${text}` : text))
        setDescriptionTouched(true)
        setMode('detailed')
        setVoicePhase('idle')
        toast.error(
          err instanceof Error
            ? `Could not draft the ticket (${err.message}). Your words are in Description.`
            : 'Could not draft the ticket. Your words are in Description.',
        )
      }
    },
    [applyDraft, toast, voiceDraftingEnabled],
  )

  const draftFromTranscript = useCallback(
    (spoken: string) =>
      draftAndArm(spoken, {
        inputMode: 'spoken',
        // Speech gets the longer window: recogniser noise means the drafted
        // fields are likelier to need a look than words the user typed.
        countdownSeconds: VOICE_COUNTDOWN_SECONDS,
      }),
    [draftAndArm],
  )

  /** "File it" in the quick composer: one sentence in, ticket out. */
  const handleQuickFile = useCallback(() => {
    if (fieldsLocked || !quickText.trim()) return
    void draftAndArm(quickText, {
      inputMode: 'typed',
      countdownSeconds: QUICK_COUNTDOWN_SECONDS,
    })
  }, [draftAndArm, fieldsLocked, quickText])

  // The two views are one report seen at two levels of detail, so switching
  // hands the sentence across rather than copying it: whichever box the user is
  // about to look at holds the text, and the other is left empty for the trip
  // back. Every other field keeps its value on both sides, so a round trip
  // through "Add details" and back loses nothing.

  /** "Add details": drop into the full form, carrying whatever was typed. */
  const openDetailedForm = useCallback(() => {
    setDescription((prev) => (prev.trim() ? prev : quickText.trim()))
    setQuickText('')
    setMode('detailed')
  }, [quickText])

  /** "Back": return to the one-box composer, carrying the description along. */
  const openQuickComposer = useCallback(() => {
    setQuickText((prev) => (prev.trim() ? prev : description.trim()))
    setDescription('')
    setDescriptionTouched(false)
    setMode('quick')
  }, [description])

  const startListening = useCallback(async () => {
    liveTranscriptRef.current = ''
    setLiveTranscript('')
    setDraftSource(null)

    const Recognition = getSpeechRecognitionCtor()
    if (Recognition) {
      voiceModeRef.current = 'browser'
      try {
        const recognition = new Recognition()
        // `interimResults` is what produces the word-by-word display; without it
        // the text only lands once the speaker stops.
        recognition.continuous = true
        recognition.interimResults = true
        recognition.lang = SPEECH_LANG

        recognition.onresult = (event) => {
          let finalText = ''
          let interimText = ''
          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i]
            const text = result?.[0]?.transcript ?? ''
            if (result.isFinal) finalText += `${text} `
            else interimText += text
          }
          const running = `${finalText}${interimText}`.trim()
          liveTranscriptRef.current = running
          setLiveTranscript(running)
        }

        recognition.onerror = (event) => {
          // A cancelled take clears the ref first, so this is a real failure.
          if (recognitionRef.current !== recognition) return
          recognitionRef.current = null
          setVoicePhase('idle')
          const code = event?.error
          if (code === 'not-allowed' || code === 'service-not-allowed') {
            toast.error(
              'Microphone access is blocked. Please allow it in your browser settings.',
            )
          } else if (code === 'no-speech') {
            toast.error('No speech detected. Please try again.')
          } else {
            toast.error(
              code ? `Speech recognition failed (${code})` : 'Speech recognition failed',
            )
          }
        }

        // Fires on an explicit stop and when the recogniser ends by itself. Both
        // mean the take is over, so draft from whatever was heard. onerror has
        // already cleared the ref when this follows a failure.
        recognition.onend = () => {
          if (recognitionRef.current !== recognition) return
          recognitionRef.current = null
          void draftFromTranscript(liveTranscriptRef.current)
        }

        recognitionRef.current = recognition
        recognition.start()
        setVoicePhase('listening')
        return
      } catch (err) {
        recognitionRef.current = null
        logger.warn('Bugsnap voice: browser recognition unavailable, recording instead', {
          error: String(err),
        })
      }
    }

    voiceModeRef.current = 'batch'
    const started = await recorder.startRecording()
    if (!started.ok) {
      toast.error(started.error || 'Could not start recording')
      setVoicePhase('idle')
      return
    }
    setVoicePhase('listening')
  }, [recorder, toast, draftFromTranscript])

  const stopListening = useCallback(async () => {
    if (voiceModeRef.current === 'browser') {
      // onend does the drafting, so the last words spoken are not dropped.
      setVoicePhase('drafting')
      try {
        recognitionRef.current?.stop()
      } catch (err) {
        logger.debug('Bugsnap voice: error stopping recognition', { error: String(err) })
        const recognition = recognitionRef.current
        recognitionRef.current = null
        if (recognition) await draftFromTranscript(liveTranscriptRef.current)
      }
      return
    }

    setVoicePhase('drafting')
    try {
      const blob = await recorder.stopRecording()
      if (!blob) {
        setVoicePhase('idle')
        toast.error('No speech detected. Please try again.')
        return
      }
      const result = await chatDictationApi.transcribe(blob)
      await draftFromTranscript(result.text)
    } catch (err) {
      setVoicePhase('idle')
      toast.error(err instanceof Error ? err.message : 'Transcription failed')
    }
  }, [recorder, draftFromTranscript, toast])

  const handleMicClick = useCallback(() => {
    if (submitting || recording || voicePhase === 'drafting') return
    if (voicePhase === 'listening') {
      void stopListening()
      return
    }
    void startListening()
  }, [submitting, recording, voicePhase, startListening, stopListening])

  // Shielded like the Bugsnap trigger and the Screenshot button: pressing the mic
  // must not reach the page's document-level listeners, or the menu the user is
  // about to describe would close underneath them. See ./useShieldedTrigger.
  const micBtnRef = useShieldedTrigger<HTMLButtonElement>(handleMicClick)

  // Auto-submit countdown. setState happens in the timer callback rather than
  // the effect body, so each tick is one render and the loop cannot cascade.
  const submitReportRef = useRef(submitReport)
  useEffect(() => {
    submitReportRef.current = submitReport
  }, [submitReport])

  useEffect(() => {
    if (voicePhase !== 'review') return
    const id = setTimeout(() => {
      if (countdown <= 1) {
        setVoicePhase('idle')
        void submitReportRef.current()
      } else {
        setCountdown(countdown - 1)
      }
    }, 1000)
    return () => clearTimeout(id)
  }, [voicePhase, countdown])

  // Any click while the countdown runs means "I want to look at this myself":
  // stop the timer and leave the report to be submitted by hand. Capture phase,
  // so a click on a field cancels before that field takes focus. Clicking Submit
  // cancels too, then submits on the same gesture — the same outcome, sooner.
  // A backdrop click also lands here, but onClose follows and discards the draft,
  // which is why the toast points at the fields rather than "anywhere".
  useEffect(() => {
    if (voicePhase !== 'review') return
    const cancelAutoSubmit = () => setVoicePhase('idle')
    document.addEventListener('pointerdown', cancelAutoSubmit, true)
    return () => document.removeEventListener('pointerdown', cancelAutoSubmit, true)
  }, [voicePhase])

  if (!isOpen) return null

  const quickMode = mode === 'quick'

  // "Back" belongs only to the full form, and only while the composer is still
  // the shorter way to say the same thing. The composer itself is the entry
  // point — the close button is the way out of it — so there is nothing to go
  // back to there. Nor after a draft has filled the fields (collapsing them into
  // one box would hide work about to be filed), nor without a drafting model.
  const canReturnToQuick =
    !quickMode && voiceDraftingEnabled && draftSource === null && voicePhase === 'idle'

  let micTitle: string
  if (voicePhase === 'listening') {
    micTitle = 'Stop and create the ticket'
  } else if (voiceDraftingEnabled) {
    micTitle = 'Speak your issue — the ticket fills itself'
  } else {
    micTitle = 'Speak your issue — dictates into Description'
  }

  let micIcon
  if (voicePhase === 'drafting') {
    micIcon = <Loader2 size={14} className="spin" />
  } else if (voicePhase === 'listening') {
    micIcon = (
      <span className="bug-report-modal__mic-live">
        <span className="bug-report-modal__mic-ripple" aria-hidden />
        <Mic size={14} />
      </span>
    )
  } else {
    micIcon = <Mic size={14} />
  }

  let micLabel: string
  if (voicePhase === 'drafting') {
    micLabel = 'Creating your ticket…'
  } else if (voicePhase === 'listening') {
    micLabel = 'Listening — click to finish'
  } else {
    micLabel = 'Speak'
  }

  let submitButtonContent
  if (submitting) {
    submitButtonContent = (
      <>
        <Loader2 size={14} className="spin" />
        Submitting…
      </>
    )
  } else {
    // The countdown used to live here as "Submitting in Ns". It now sits beside
    // the bar, where it can carry the sentence that says how to stop it — so the
    // button is just the manual submit again, pulsing to show it is about to
    // fire on its own.
    submitButtonContent = 'Submit report'
  }

  // Render via a portal to <body> so the fixed-position overlay is measured
  // against the viewport. Mounting inline can place it inside an ancestor with
  // `transform`/`filter`/`backdrop-filter` (e.g. the ontology canvas chrome's
  // backdrop blur), which creates a containing block that clips the dialog to
  // the toolbar height — rendering it as an empty sliver at the top.
  return createPortal(
    <div
      className={`bug-report-modal${recording ? ' bug-report-modal--recording-active' : ''}`}
      // Marks this subtree as Bugsnap's own chrome: page UI that opts in keeps its
      // open state while the user fills in the report, so the screenshot taken from
      // here still shows it. See ./bugsnapUi.
      {...BUGSNAP_UI_PROPS}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bug-report-title"
    >
      {recording ? (
        <button
          type="button"
          className="bug-report-modal__recording-stop"
          onClick={stopScreenRecording}
          aria-label="Stop recording"
        >
          <span className="bug-report-modal__recording-dot" aria-hidden />
          <Square size={14} />
          Stop recording
        </button>
      ) : (
        <>
          <div className="bug-report-modal__backdrop" onClick={onClose} aria-hidden />
          <div className="bug-report-modal__panel">
        <header className="bug-report-modal__header">
          <div className="bug-report-modal__title-row">
            {canReturnToQuick ? (
              // Takes the icon's slot rather than sitting beside it: the header
              // is a tight row, and a back arrow reads as the thing you came in
              // through, which is exactly what the bug glyph would otherwise be.
              <button
                type="button"
                className="bug-report-modal__back"
                onClick={openQuickComposer}
                title="Back to the quick report"
                aria-label="Back to the quick report"
              >
                <ArrowLeft size={18} />
              </button>
            ) : (
              <Bug size={18} aria-hidden />
            )}
            <h2 id="bug-report-title">Bugsnap</h2>
            {voiceSupported && (
              <button
                ref={micBtnRef}
                type="button"
                className={`bug-report-modal__mic${
                  voicePhase === 'listening' ? ' bug-report-modal__mic--listening' : ''
                }`}
                // Fallback only: the shield normally swallows the click first.
                onClick={handleMicClick}
                disabled={submitting || recording || voicePhase === 'drafting'}
                title={micTitle}
                aria-label={
                  voicePhase === 'listening'
                    ? 'Stop recording and create the ticket'
                    : 'Speak your issue'
                }
              >
                {micIcon}
                <span className="bug-report-modal__mic-label">{micLabel}</span>
              </button>
            )}
          </div>
          <button type="button" className="bug-report-modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {voiceBusy && (
          <div className="bug-report-modal__transcript" aria-live="polite">
            {liveTranscript ||
              (voicePhase === 'drafting' ? 'Filling the form…' : 'Describe what went wrong…')}
          </div>
        )}

        {draftSource === 'heuristic' && !voiceBusy && (
          <div className="bug-report-modal__voice-note" role="status">
            AI drafting was unavailable, so this is your transcript as spoken. Please
            check the title and severity before it is filed.
          </div>
        )}

        <form className="bug-report-modal__form" onSubmit={handleSubmit} noValidate>
          <div
            className={`bug-report-modal__body${
              voiceBusy ? ' bug-report-modal__body--locked' : ''
            }`}
          >
          {quickMode ? (
            <label className="bug-report-modal__field">
              <span>What went wrong?</span>
              <textarea
                value={quickText}
                onChange={(e) => setQuickText(e.target.value)}
                // Enter alone stays a newline — a two-line report is normal —
                // so the keyboard path out of the box is the usual modifier.
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    handleQuickFile()
                  }
                }}
                placeholder="One or two sentences — e.g. “saved workflow keeps spinning on the canvas, blocking my demo”"
                rows={4}
                maxLength={QUICK_TEXT_MAX_CHARS}
                disabled={fieldsLocked}
                autoFocus
              />
              <span className="bug-report-modal__quick-hint">
                AI writes the title, description and severity. The page, your user and the
                captured logs are attached automatically.
              </span>
            </label>
          ) : (
          <>
          <label
            className={`bug-report-modal__field${titleError ? ' bug-report-modal__field--invalid' : ''}`}
          >
            <span>
              Title<span className="bug-report-modal__required-asterisk"> *</span>
            </span>
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              placeholder="Short summary of the issue"
              maxLength={255}
              required
              disabled={fieldsLocked}
              aria-invalid={titleError ? true : undefined}
              aria-describedby={titleError ? 'bug-report-title-error' : undefined}
            />
            {titleError ? (
              <span id="bug-report-title-error" className="bug-report-modal__field-error" role="alert">
                {titleError}
              </span>
            ) : null}
          </label>

          <label className="bug-report-modal__field">
            <span>
              Description<span className="bug-report-modal__required-asterisk"> *</span>
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => setDescriptionTouched(true)}
              placeholder="Steps to reproduce, expected vs actual behavior…"
              rows={5}
              required
              disabled={fieldsLocked}
            />
            {showDescriptionError && <span className="bug-report-modal__error-message">{descriptionError}</span>}
          </label>

          <div className="bug-report-modal__row">
            <div className="bug-report-modal__field">
              <span>
                Severity<span className="bug-report-modal__required-asterisk"> *</span>
              </span>
              <SearchableDropdown
                options={SEVERITY_OPTIONS.filter((o) => o.value).map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                value={severity}
                placeholder="Select severity..."
                onChange={(val) => {
                  setSeverity(val)
                  setSeverityTouched(true)
                }}
                disabled={fieldsLocked}
                hideSearch
                clearable={false}
                usePortal
                className={`bug-report-modal__dropdown${showSeverityError ? ' bug-report-modal__dropdown--error' : ''}`}
              />
              {showSeverityError && <span className="bug-report-modal__error-message">{severityError}</span>}
            </div>

            <div className="bug-report-modal__field">
              <span>Type</span>
              <SearchableDropdown
                options={WORK_ITEM_TYPE_OPTIONS.map((t) => ({ value: t, label: t }))}
                value={workItemType}
                onChange={(val) => setWorkItemType(val)}
                disabled={fieldsLocked}
                hideSearch
                clearable={false}
                usePortal
                className="bug-report-modal__dropdown"
              />
            </div>
          </div>

          <div className="bug-report-modal__row">
            <label className="bug-report-modal__field">
              <span>Feature Group</span>
              <input
                type="text"
                value={featureGroup}
                onChange={(e) => setFeatureGroup(e.target.value)}
                placeholder="Auto-detected from page"
                maxLength={200}
                disabled={fieldsLocked}
              />
            </label>

            <label className="bug-report-modal__field">
              <span>Workflow/Agent ID</span>
              <input
                type="text"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                placeholder="Auto-detected from page URL"
                maxLength={200}
                disabled={fieldsLocked}
              />
            </label>
          </div>

          <label className="bug-report-modal__field">
            <span>Feature</span>
            <input
              type="text"
              value={feature}
              onChange={(e) => setFeature(e.target.value)}
              placeholder="Specific feature affected (optional)"
              maxLength={500}
              disabled={fieldsLocked}
            />
          </label>

          <div className="bug-report-modal__row">
            <label className="bug-report-modal__field">
              <span>Other Details</span>
              <textarea
                value={otherDetails}
                onChange={(e) => setOtherDetails(e.target.value)}
                placeholder="Any extra context (optional)"
                rows={2}
                maxLength={2000}
                disabled={fieldsLocked}
              />
            </label>

            <label className="bug-report-modal__field">
              <span>Suggestions</span>
              <textarea
                value={suggestions}
                onChange={(e) => setSuggestions(e.target.value)}
                placeholder="Proposed fix or improvement (optional)"
                rows={2}
                maxLength={2000}
                disabled={fieldsLocked}
              />
            </label>
          </div>
          </>
          )}

          {/* Attachments belong to both views: a screenshot is often the whole
              report, and there is no reason to make the user open the full form
              to add one. */}
          <div className="bug-report-modal__attachments">
            <span className="bug-report-modal__attachments-label">Attachments</span>
            <div className="bug-report-modal__attach-actions">
              <button
                ref={screenshotBtnRef}
                type="button"
                className="bug-report-modal__attach-btn"
                // Fallback only: the shield normally swallows the click before React sees it.
                onClick={handleCaptureScreenshot}
                disabled={fieldsLocked || capturingScreenshot || recording}
              >
                {capturingScreenshot ? <Loader2 size={14} className="spin" /> : <Camera size={14} />}
                Screenshot
              </button>
              <button
                type="button"
                className="bug-report-modal__attach-btn"
                onClick={() => void startScreenRecording()}
                disabled={fieldsLocked || capturingScreenshot}
              >
                <MonitorPlay size={14} />
                Record screen
              </button>
              <button
                type="button"
                className="bug-report-modal__attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={fieldsLocked || recording}
              >
                <Paperclip size={14} />
                Upload file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,application/pdf,text/plain"
                multiple
                hidden
                onChange={onFileInputChange}
              />
            </div>

            {attachments.length > 0 && (
              <ul className="bug-report-modal__file-list">
                {attachments.map((a) => (
                  <li key={a.id}>
                    {a.previewUrl ? (
                      <button
                        type="button"
                        className="bug-report-modal__thumb-btn"
                        onClick={() => setPreviewId(a.id)}
                        title="Click to preview"
                        aria-label={`Preview ${a.file.name}`}
                      >
                        {isVideoFile(a.file) ? (
                          <>
                            {/* `#t=0.1` seeks past frame zero: without it a freshly
                                recorded webm has no decoded frame to paint and the
                                tile renders as an empty black square. */}
                            <motion.video
                              layoutId={`preview-${a.id}`}
                              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                              src={`${a.previewUrl}#t=0.1`}
                              className="bug-report-modal__thumb"
                              preload="metadata"
                              muted
                              playsInline
                            />
                            <span className="bug-report-modal__thumb-badge" aria-hidden>
                              <Play size={12} />
                            </span>
                          </>
                        ) : (
                          <motion.img 
                            layoutId={`preview-${a.id}`}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            src={a.previewUrl} 
                            alt="" 
                            className="bug-report-modal__thumb" 
                          />
                        )}
                      </button>
                    ) : (
                      <span className="bug-report-modal__file-icon">
                        {a.kind === 'recording' ? <Video size={16} /> : <Paperclip size={16} />}
                      </span>
                    )}
                    <span className="bug-report-modal__file-name" title={a.file.name}>
                      {a.file.name}
                    </span>
                    {a.previewUrl && (
                      <button
                        type="button"
                        className="bug-report-modal__file-preview"
                        onClick={() => setPreviewId(a.id)}
                        title="Preview"
                        aria-label={`Preview ${a.file.name}`}
                      >
                        <Eye size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="bug-report-modal__file-remove"
                      onClick={() => removeAttachment(a.id)}
                      aria-label={`Remove ${a.file.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          </div>

          <footer className="bug-report-modal__footer">
            <div className="bug-report-modal__hint">
              <Info size={14} className="bug-report-modal__hint-icon" />
              <span>
                Reports are sent to {getBugReportTargetName()}. Page URL, user, and app version (
                {config.app.version}) are included automatically.
              </span>
            </div>
            {voicePhase === 'review' && (
              // The bar alone only says "something is running out". The sentence
              // beside it says what happens at the end and how to stop it —
              // inline and persistent, because a toast is missable and the
              // fields it talks about are right here.
              <div className="bug-report-modal__countdown">
                <p className="bug-report-modal__countdown-note">
                  Submitting automatically in{' '}
                  {/* Only the number is live, so a screen reader announces the
                      seconds rather than re-reading the whole sentence each tick. */}
                  <strong aria-live="polite">{countdown}s</strong> — click any field to
                  stop the timer and edit.
                </p>
                {/* Drains in step with the digits above: both are driven by the
                    same window, the bar via its inline duration. */}
                <div className="bug-report-modal__countdown-track" aria-hidden>
                  <span
                    className="bug-report-modal__countdown-fill"
                    style={{ animationDuration: `${countdownFrom}s` }}
                  />
                </div>
              </div>
            )}
            {quickMode ? (
              <>
                <button
                  type="button"
                  className="bug-report-modal__btn bug-report-modal__btn--ghost"
                  onClick={openDetailedForm}
                  disabled={fieldsLocked}
                >
                  <SlidersHorizontal size={14} />
                  Add details
                </button>
                {/* Not type="submit": the form has no title or severity yet.
                    Drafting produces them, and the countdown submits. */}
                <button
                  type="button"
                  className="bug-report-modal__btn bug-report-modal__btn--primary"
                  onClick={handleQuickFile}
                  disabled={fieldsLocked || !quickText.trim()}
                >
                  {voicePhase === 'drafting' ? (
                    <>
                      <Loader2 size={14} className="spin" />
                      Creating your ticket…
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      File it
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="bug-report-modal__btn bug-report-modal__btn--ghost" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`bug-report-modal__btn bug-report-modal__btn--primary${
                    voicePhase === 'review' ? ' bug-report-modal__btn--counting' : ''
                  }`}
                  disabled={
                    fieldsLocked || Boolean(titleError) || !title.trim() || !description.trim() || !severity
                  }
                >
                  {submitButtonContent}
                </button>
              </>
            )}
          </footer>
        </form>
          </div>
        </>
      )}

      {/* Full-size preview of a capture. Rendered inside the Bugsnap root so it
          stacks over the form and is excluded from the screenshot filter along
          with the rest of the dialog's own chrome. */}
      <AnimatePresence>
        {previewItem && (
          <motion.div
            className="bug-report-modal__preview"
            role="dialog"
            aria-modal="true"
            aria-label={`Preview of ${previewItem.file.name}`}
            onClick={closePreview}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(4px)',
              cursor: 'zoom-out',
              padding: 0
            }}
          >
            {isVideoFile(previewItem.file) ? (
              <motion.video
                layoutId={`preview-${previewItem.id}`}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                key={previewItem.id}
                src={previewItem.previewUrl}
                className="bug-report-modal__preview-media"
                controls
                autoPlay
                playsInline
                style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px', cursor: 'default' }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <motion.img
                layoutId={`preview-${previewItem.id}`}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                key={previewItem.id}
                src={previewItem.previewUrl}
                alt={previewItem.file.name}
                className="bug-report-modal__preview-media"
                style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px', cursor: 'default' }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <motion.button
              type="button"
              className="bug-report-modal__preview-close"
              onClick={closePreview}
              aria-label="Close preview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ position: 'absolute', top: 24, right: 24, background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', padding: 8, borderRadius: '50%', cursor: 'pointer' }}
            >
              <X size={20} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  )
}
